import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface EGiftCardCostSummaryItem {
  id: string | number;
  name: string;
  quantity: number;
  price: number;
  originalQuantity?: number;
}

interface EGiftCardCostSummaryProps {
  items: EGiftCardCostSummaryItem[];
  isEditMode?: boolean;
}

const getDifferenceClassName = (difference: number) => {
  if (difference > 0) return "text-emerald-600 dark:text-emerald-400";
  if (difference < 0) return "text-red-600 dark:text-red-400";
  return "text-gray-500 dark:text-gray-400";
};

const formatSignedQuantity = (quantity: number) => {
  if (quantity > 0) return `+${quantity}`;
  if (quantity < 0) return String(quantity);
  return "±0";
};

export const EGiftCardCostSummary = ({
  items,
  isEditMode = false,
}: EGiftCardCostSummaryProps) => {
  const { t, i18n } = useTranslation();
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.resolvedLanguage ?? i18n.language, {
        style: "currency",
        currency: "USD",
      }),
    [i18n.language, i18n.resolvedLanguage],
  );
  const totalAmount = items.reduce(
    (total, card) => total + card.price * card.quantity,
    0,
  );
  const costDifference = items.reduce(
    (total, card) =>
      total + card.price * (card.quantity - (card.originalQuantity ?? card.quantity)),
    0,
  );
  const formatSignedCurrency = (amount: number) => {
    if (amount > 0) return `+${currencyFormatter.format(amount)}`;
    if (amount < 0) return `-${currencyFormatter.format(Math.abs(amount))}`;
    return currencyFormatter.format(0);
  };

  return (
    <div className="rounded-[10px] border border-l-4 border-[#E6E6E6] border-l-[#00C49A] bg-white p-6 dark:border-gray-600 dark:border-l-[#00C49A] dark:bg-gray-800">
      <h3 className="mb-4 text-base text-[#004232] dark:text-white">
        {t("createEGiftCard.costSummary")}
      </h3>

      <div className="space-y-3">
        {items.map((card) => {
          const quantityDifference =
            card.quantity - (card.originalQuantity ?? card.quantity);
          const amountDifference = card.price * quantityDifference;
          const differenceClassName = getDifferenceClassName(quantityDifference);

          return (
            <div
              key={card.id}
              className="flex flex-wrap items-center justify-between gap-2"
            >
              <span className="text-sm text-[#004232] dark:text-white">{card.name}</span>
              <div className="flex items-center gap-3">
                <span className="rounded-md bg-[#00C49A] px-2 py-1 text-xs text-white">
                  {t("createEGiftCard.quantityShort")}: {card.quantity}
                  {isEditMode && (
                    <span className="ml-1 rounded bg-white/20 px-1" title={t("createEGiftCard.quantityDifference")}>
                      {formatSignedQuantity(quantityDifference)}
                    </span>
                  )}
                </span>
                <div className="min-w-[90px] text-right">
                  <span className="block text-sm text-[#004232] dark:text-white">
                    {currencyFormatter.format(card.price * card.quantity)}
                  </span>
                  {isEditMode && (
                    <span className={`block text-xs font-semibold ${differenceClassName}`}>
                      <span className="sr-only">
                        {t("createEGiftCard.costDifference")}: {" "}
                      </span>
                      {formatSignedCurrency(amountDifference)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <hr className="my-4 border-[#DCDCDC] dark:border-gray-600" />

      <div className="flex items-center justify-between">
        <span className="text-base font-bold text-[#0A0A0A] dark:text-white">
          {t("createEGiftCard.totalCost")}
        </span>
        <span className="text-xl font-bold text-[#0A0A0A] dark:text-white">
          {currencyFormatter.format(totalAmount)}
        </span>
      </div>

      {isEditMode && (
        <div className="mt-3 flex items-center justify-between border-t border-dashed border-[#DCDCDC] pt-3 dark:border-gray-600">
          <span className="text-sm font-semibold text-[#0A0A0A] dark:text-white">
            {t("createEGiftCard.costDifference")}
          </span>
          <span className={`text-lg font-bold ${getDifferenceClassName(costDifference)}`}>
            {formatSignedCurrency(costDifference)}
          </span>
        </div>
      )}
    </div>
  );
};
